import React from "react";
import { X, RotateCcw, RotateCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ExteriorImage, DamageImage } from "@/types/vehicleTest";
import type { FileObject } from "@/types/shared/file";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";

interface ImageGridProps {
  images: (ExteriorImage | DamageImage | FileObject)[];
  section: string;
  onImageClick: (image: any, alt: string) => void; // Changed to pass full image object
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (index: number, tireType?: "primary" | "secondary") => void;
  tireType?: "primary" | "secondary";
  /** Visa index (1, 2, 3…) på varje bild */
  showIndex?: boolean;
  /** Rotera bild 90° vänster/höger; sparas i backend när testId finns */
  onRotate?: (image: any, direction: "left" | "right") => void;
}

export const ImageGrid: React.FC<ImageGridProps> = ({
  images,
  section,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  tireType,
  showIndex = false,
  onRotate,
}) => {
  const { language } = useLanguage();
  const L = listingPostLabels(language);
  return (
    <>
      {images.map((img, index) => (
        <div key={img.id || index} className="relative">
          <div className="relative group">
            <img
              src={getThumbnailUrl ? getThumbnailUrl(img) : img.url}
              alt={`${section} ${index + 1}`}
              className={`w-full h-24 object-cover rounded border cursor-pointer ${
                section === "exterior" && index === 0
                  ? "border-blue-500 border-2"
                  : ""
              }`}
              onClick={() => onImageClick(img, `${section} ${index + 1}`)}
              loading="lazy"
            />
            {showIndex && (
              <Badge
                variant="secondary"
                className="absolute top-1 left-1 h-6 min-w-6 flex items-center justify-center rounded-full bg-black/70 text-white text-xs font-semibold"
              >
                {index + 1}
              </Badge>
            )}
            {onRotate && (
              <div className="absolute bottom-1 left-1 flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotate(img, "left");
                  }}
                  className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                  aria-label={L.images?.rotateLeft ?? "Rotera vänster"}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotate(img, "right");
                  }}
                  className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                  aria-label={L.images?.rotateRight ?? "Rotera höger"}
                >
                  <RotateCw className="h-4 w-4" />
                </button>
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemoveImage(index, tireType);
              }}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
            >
              <X className="h-5 w-5" />
            </button>
            {section === "exterior" && index === 0 && (
              <Badge
                variant="secondary"
                className="absolute -top-1 -left-1 bg-blue-500 text-white text-xs"
              >
                {L.images?.mainImage ?? "Huvudbild"}
              </Badge>
            )}
          </div>
        </div>
      ))}
    </>
  );
};
